/**
 * VistoeraTASK-00582 — resolve which Content Project to open for a Canvas entry.
 *
 * Order (under the CURRENT session only):
 * 1. user+workspace last-opened hint (localStorage) — validated by the server;
 * 2. canonical Home Canvas (`getHomeCanvas`) — idempotent blank/home surface,
 *    never invents a second empty content project on every visit.
 *
 * Temporary network errors throw with `retryable: true` and do NOT clear the
 * last-opened hint. Deleted / forbidden hints are cleared and fall through.
 *
 * VistoeraTASK-00576 - only a project the CURRENT account can edit is ever
 * resumed. A hint that points at someone else's (shared, read-only) project
 * is dropped, so a second account is never dropped into another account's
 * Canvas; it lands on its own Home Canvas instead.
 *
 * API imports are lazy so node tests can inject deps without loading Vite env.
 */
import { isOwnCanvasProject, unwrapProjectDetail } from '../navigation/canvasProjectOwnership'
import {
  clearLastOpenedCanvas,
  readLastOpenedCanvas,
} from '../navigation/lastOpenedCanvas'

export class CanvasEntryResolveError extends Error {
  readonly retryable: boolean
  constructor(message: string, retryable: boolean) {
    super(message)
    this.name = 'CanvasEntryResolveError'
    this.retryable = retryable
  }
}

export interface CanvasEntryResolveDeps {
  readHint?: (userId: string, workspaceId: string) => string | null
  clearHint?: (userId: string, workspaceId: string) => void
  probeProject?: (workspaceId: string, projectId: string) => Promise<unknown>
  /** Decides whether a probed project may be silently resumed (default: OWNED by me). */
  canResume?: (project: unknown, userId: string) => boolean
  resolveHome?: (workspaceId: string) => Promise<{ project_id: string }>
}

type ApiErr = { status?: number; code?: string; message?: string }

function isNotFoundOrForbidden(err: ApiErr): boolean {
  return err.status === 404 || err.status === 403 || err.code === 'NOT_FOUND'
}

/** VistoeraTASK-00577 — resume only projects OWNED by the signed-in account
 * (own project / own Home Canvas). Visibility or edit rights alone never qualify, so
 * a hint left by older builds that points at a teammate's project is rejected. */
export function defaultCanResume(project: unknown, userId: string): boolean {
  return isOwnCanvasProject(unwrapProjectDetail(project), userId)
}

async function defaultProbeProject(workspaceId: string, projectId: string): Promise<unknown> {
  const { getContentProject } = await import('../api')
  return (await getContentProject(workspaceId, projectId)).data
}

async function defaultResolveHome(workspaceId: string): Promise<{ project_id: string }> {
  const { getHomeCanvas } = await import('../api')
  return { project_id: (await getHomeCanvas(workspaceId)).data.project_id }
}

export async function resolveCanvasEntryProjectId(
  workspaceId: string,
  userId: string,
  deps: CanvasEntryResolveDeps = {},
): Promise<string> {
  const readHint = deps.readHint ?? readLastOpenedCanvas
  const clearHint = deps.clearHint ?? clearLastOpenedCanvas
  const probeProject = deps.probeProject ?? defaultProbeProject
  const resolveHome = deps.resolveHome ?? defaultResolveHome
  const canResume = deps.canResume ?? defaultCanResume

  const hinted = readHint(userId, workspaceId)
  if (hinted) {
    try {
      const probed = await probeProject(workspaceId, hinted)
      if (canResume(probed, userId)) return hinted
      clearHint(userId, workspaceId)
    } catch (err) {
      const apiErr = err as ApiErr
      if (isNotFoundOrForbidden(apiErr)) {
        clearHint(userId, workspaceId)
      } else {
        throw new CanvasEntryResolveError(apiErr.message || 'Failed to open last canvas', true)
      }
    }
  }

  try {
    const home = await resolveHome(workspaceId)
    return home.project_id
  } catch (err) {
    const apiErr = err as ApiErr
    throw new CanvasEntryResolveError(apiErr.message || 'Failed to open Home Canvas', true)
  }
}
